import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, TrendingDown, Home, DollarSign, Wrench,
  AlertTriangle, ArrowRight, Users, Activity,
} from "lucide-react";
import { fmt } from "@/hooks/useREInvestingSim";
import { getRealm } from "@/components/lunimar/lunimarRealms";
import { SimWalletImpact, ReadinessMeter } from "@/components/lunimar-kit";

interface RoundEvent {
  type: string;
  property?: string;
  message: string;
  cost?: number;
  from?: string;
  to?: string;
}

interface Props {
  summary: {
    round: number;
    cycle: string;
    prev_cycle: string;
    rent_collected: number;
    expenses_paid: number;
    repair_costs: number;
    appreciation: number;
    net_income: number;
    cash_balance: number;
    portfolio_value: number;
    total_debt: number;
    events: number;
    occupied_count: number;
    total_count: number;
    round_events?: RoundEvent[];
    triggered_event?: unknown;
  };
  onContinue: () => void;
  hasEvent: boolean;
}

const CYCLE_LABELS: Record<string, { label: string; hsl: string; icon: typeof TrendingUp }> = {
  expansion: { label: "Expansion",  hsl: "152 70% 55%", icon: TrendingUp },
  stable:    { label: "Stable",     hsl: "188 88% 58%", icon: Activity },
  slowdown:  { label: "Slowdown",   hsl: "38 95% 60%",  icon: TrendingDown },
  downturn:  { label: "Downturn",   hsl: "0 78% 60%",   icon: TrendingDown },
  recovery:  { label: "Recovery",   hsl: "262 78% 64%", icon: TrendingUp },
};

function Stat({ label, value, hsl }: { label: string; value: string; hsl: string }) {
  return (
    <div
      className="rounded-xl border p-2.5 backdrop-blur-sm"
      style={{
        borderColor: `hsl(${hsl} / 0.35)`,
        background: `hsl(${hsl} / 0.10)`,
      }}
    >
      <p className="text-[10px] font-mono uppercase tracking-widest text-white/55">{label}</p>
      <p className="mt-0.5 font-heading text-sm font-bold tabular-nums" style={{ color: `hsl(${hsl})` }}>{value}</p>
    </div>
  );
}

export default function RoundSummary({ summary, onContinue, hasEvent }: Props) {
  const realm = getRealm("re_investing");
  const cycle = CYCLE_LABELS[summary.cycle] || CYCLE_LABELS.stable;
  const CycleIcon = cycle.icon;
  const cycleChanged = summary.cycle !== summary.prev_cycle;
  const occupancyPct = summary.total_count > 0
    ? Math.round((summary.occupied_count / summary.total_count) * 100)
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-3xl border p-5 backdrop-blur-md sm:p-6"
        style={{
          borderColor: `hsl(${realm.primaryAccent} / 0.35)`,
          background: `linear-gradient(160deg, hsl(${realm.primaryAccent} / 0.10), hsl(228 45% 10% / 0.85))`,
          boxShadow: `0 40px 80px -40px hsl(${realm.primaryAccent} / 0.5)`,
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-25 blur-3xl"
          style={{ background: `hsl(${realm.primaryAccent})` }}
        />

        <div className="relative space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <div
                className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest"
                style={{ color: `hsl(${realm.primaryAccent})` }}
              >
                <Activity className="h-3 w-3" /> Quarter Debrief
              </div>
              <h3 className="mt-0.5 font-heading text-lg font-bold text-white">
                Month {summary.round}
              </h3>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span
                  className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider"
                  style={{
                    borderColor: `hsl(${cycle.hsl} / 0.5)`,
                    background: `hsl(${cycle.hsl} / 0.12)`,
                    color: `hsl(${cycle.hsl})`,
                  }}
                >
                  <CycleIcon className="h-2.5 w-2.5" /> {cycle.label} cycle
                </span>
                {cycleChanged && (
                  <span className="rounded-md border border-white/20 bg-white/5 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-white/80">
                    Cycle shift
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Net income — primary outcome */}
          <SimWalletImpact
            realm={realm}
            delta={summary.net_income}
            persisted={false}
            label="Net quarterly cash flow"
          />

          {/* Occupancy meter */}
          <ReadinessMeter
            realm={realm}
            value={occupancyPct}
            label="Occupancy"
            outcome={`${summary.occupied_count}/${summary.total_count} units`}
          />

          {/* Financial breakdown grid */}
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Rent collected" value={`+${fmt(summary.rent_collected)}`} hsl="152 70% 55%" />
            <Stat label="Expenses paid"  value={`-${fmt(summary.expenses_paid)}`}   hsl="0 78% 60%" />
            {summary.repair_costs > 0 && (
              <Stat label="Repair costs" value={`-${fmt(summary.repair_costs)}`} hsl="38 95% 60%" />
            )}
            <Stat label="Appreciation" value={`+${fmt(summary.appreciation)}`} hsl={realm.primaryAccent} />
          </div>

          {/* Events this round */}
          {summary.round_events && summary.round_events.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/55">
                Live signals
              </div>
              {summary.round_events.map((evt: RoundEvent, i: number) => (
                <div key={i} className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/5 p-2">
                  {evt.type === "vacancy" ? <Users className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: "hsl(0 78% 60%)" }} /> :
                   evt.type === "tenant_found" ? <Users className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: "hsl(152 70% 55%)" }} /> :
                   evt.type === "repair" ? <Wrench className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: "hsl(38 95% 60%)" }} /> :
                   evt.type === "market_shift" ? <TrendingUp className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: `hsl(${realm.primaryAccent})` }} /> :
                   <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/50" />}
                  <p className="text-xs leading-relaxed text-white/80">{evt.message}</p>
                </div>
              ))}
            </div>
          )}

          {/* Portfolio snapshot */}
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Cash"      value={fmt(summary.cash_balance)}   hsl={realm.primaryAccent} />
            <Stat label="Portfolio" value={fmt(summary.portfolio_value)} hsl={realm.secondaryAccent} />
            <Stat label="Debt"      value={fmt(summary.total_debt)}     hsl="0 0% 100%" />
          </div>

          <Button onClick={onContinue} className="w-full gap-2">
            {hasEvent ? "View incoming signal" : "Continue"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
